import { useEffect, useSyncExternalStore } from 'react'
import { mergeRows, type Fetch, type Loader, type Query, type Raw } from './statisticsData'

/**
 * The page's in-memory cache, keyed by (path, query) like the CRM's stxCache:
 * the first component that needs a call starts it, every component showing it
 * re-renders when it settles, and going back to a view is instant. Each key
 * carries a generation so a response that lost a race with an invalidation
 * (retry, a withdrawal) is dropped instead of overwriting newer data.
 */

export interface Entry<T = Raw> {
  status: 'loading' | 'ok' | 'error'
  data: T | null
  error: string
}

export interface ListState {
  rows: Raw[]
  cursor: unknown
  hasMore: boolean
  loading: boolean
  loaded: boolean
  error: string
}

const LOADING: Entry<never> = { status: 'loading', data: null, error: '' }
const LIST_LOADING: ListState = { rows: [], cursor: null, hasMore: true, loading: true, loaded: false, error: '' }

export const statKey = (path: string, query?: Query) => `${path}?${JSON.stringify(query ?? {})}`

const messageOf = (e: unknown) => (e instanceof Error && e.message ? e.message : 'Request failed')

export class StatsStore {
  private entries = new Map<string, Entry<unknown>>()
  private lists = new Map<string, ListState>()
  private loaders = new Map<string, Loader>()
  private gens = new Map<string, number>()
  private listeners = new Set<() => void>()
  private fetcher: Fetch

  constructor(fetcher: Fetch) {
    this.fetcher = fetcher
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  /** Bumps on every change: a snapshot for hooks that read several keys. */
  version = 0

  private emit() {
    this.version += 1
    this.listeners.forEach((fn) => fn())
  }

  private bump(key: string) {
    const gen = (this.gens.get(key) ?? 0) + 1
    this.gens.set(key, gen)
    return gen
  }

  /** An uncached call (list pages). */
  call: Fetch = (path, query) => this.fetcher(path, query)

  get(key: string): Entry<unknown> | undefined {
    return this.entries.get(key)
  }

  /** Starts the call unless it is cached or in flight. */
  ensure(path: string, query: Query = {}) {
    const key = statKey(path, query)
    if (this.entries.has(key)) return
    const gen = this.bump(key)
    this.entries.set(key, LOADING)
    this.fetcher(path, query).then(
      (data) => this.settle(key, gen, { status: 'ok', data, error: '' }),
      (e) => this.settle(key, gen, { status: 'error', data: null, error: messageOf(e) }),
    )
    this.emit()
  }

  private settle(key: string, gen: number, entry: Entry<unknown>) {
    if (this.gens.get(key) !== gen) return
    this.entries.set(key, entry)
    this.emit()
  }

  /** A one-off read through the cache (e.g. the "All time" start date). */
  async read(path: string, query: Query = {}): Promise<unknown> {
    const key = statKey(path, query)
    const cached = this.entries.get(key)
    if (cached?.status === 'ok') return cached.data
    const data = await this.fetcher(path, query)
    this.bump(key)
    this.entries.set(key, { status: 'ok', data, error: '' })
    this.emit()
    return data
  }

  /** Replaces a cached answer (optimistic updates). */
  patch(key: string, data: unknown) {
    this.bump(key)
    this.entries.set(key, { status: 'ok', data, error: '' })
    this.emit()
  }

  /** Forgets matching calls and lists; the views showing them ask again. */
  invalidate(match: (key: string) => boolean) {
    for (const key of [...this.entries.keys()]) {
      if (!match(key)) continue
      this.bump(key)
      this.entries.delete(key)
    }
    for (const key of [...this.lists.keys()]) {
      if (!match(key)) continue
      this.bump(`list:${key}`)
      this.lists.delete(key)
      this.loaders.delete(key)
    }
    this.emit()
  }

  /** Retry: failed calls are forgotten (and so asked again), failed lists resume. */
  retry() {
    this.invalidate((key) => this.entries.get(key)?.status === 'error')
    for (const [key, list] of this.lists) {
      if (!list.error) continue
      this.lists.set(key, { ...list, error: '' })
      void this.loadMore(key)
    }
    this.emit()
  }

  // ── Paged lists ────────────────────────────────────────────────────────

  getList(key: string): ListState | undefined {
    return this.lists.get(key)
  }

  startList(key: string, loader: Loader) {
    if (this.lists.has(key)) return
    this.loaders.set(key, loader)
    this.lists.set(key, { ...LIST_LOADING, loading: false })
    void this.loadMore(key)
  }

  async loadMore(key: string) {
    const list = this.lists.get(key)
    const loader = this.loaders.get(key)
    if (!list || !loader || list.loading || !list.hasMore || list.error) return
    const genKey = `list:${key}`
    const gen = this.gens.get(genKey) ?? 0
    this.lists.set(key, { ...list, loading: true, error: '' })
    this.emit()
    try {
      const page = await loader(list.cursor)
      if ((this.gens.get(genKey) ?? 0) !== gen) return
      const current = this.lists.get(key)!
      const rows = mergeRows(current.rows, page.rows)
      const fresh = rows.length - current.rows.length
      this.lists.set(key, { ...current, rows, cursor: page.cursor, hasMore: Boolean(page.hasMore) && fresh > 0, loading: false, loaded: true })
    } catch (e) {
      if ((this.gens.get(genKey) ?? 0) !== gen) return
      this.lists.set(key, { ...this.lists.get(key)!, loading: false, loaded: true, error: messageOf(e) })
    }
    this.emit()
  }
}

/** One call's state; starts it on first use. `enabled: false` waits (state stays loading). */
export function useStat<T = Raw>(store: StatsStore, path: string, query?: Query, enabled = true): Entry<T> {
  const key = statKey(path, query)
  const entry = useSyncExternalStore(store.subscribe, () => store.get(key))
  useEffect(() => {
    if (enabled && !entry) store.ensure(path, query)
  })
  return (entry ?? LOADING) as Entry<T>
}

/** Several calls at once (all start together); same order as `calls`. */
export function useStatAll<T = Raw>(store: StatsStore, calls: [path: string, query?: Query][]): Entry<T>[] {
  useSyncExternalStore(store.subscribe, () => store.version)
  const entries = calls.map(([path, query]) => store.get(statKey(path, query)))
  useEffect(() => {
    calls.forEach(([path, query], i) => {
      if (!entries[i]) store.ensure(path, query)
    })
  })
  return entries.map((e) => (e ?? LOADING) as Entry<T>)
}

/** A paged list; the first page starts on first use. */
export function useStatList(store: StatsStore, key: string, loader: Loader): ListState {
  const list = useSyncExternalStore(store.subscribe, () => store.getList(key))
  useEffect(() => {
    if (!list) store.startList(key, loader)
  })
  return list ?? LIST_LOADING
}

/** Loading until all settle, error if any failed (the CRM's stxState). */
export function combined(...entries: Entry<unknown>[]): { loading?: boolean; error?: string; ok?: boolean } {
  const failed = entries.find((e) => e.status === 'error')
  if (failed) return { error: failed.error }
  if (entries.some((e) => e.status === 'loading')) return { loading: true }
  return { ok: true }
}
